import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  getBaseItems,
  createOrUpdateItem,
  deleteBaseItem,
  getProjectItems,
  createOrUpdateProjectItem,
  deleteProjectItem,
  createProjectItemsHeader,
  getProjectItemsHeader,
} from "../thunks/baseItemThunks";
import {
  BaseItemDTO,
  ProjectItemDTO,
  ProjectItemsHeaderDTO,
} from "../../models/BaseItem.model";
import { projectSwitched } from "../actions/projectLifecycleActions";

interface BaseItemState {
  items: BaseItemDTO[];
  projectItems: ProjectItemDTO[];
  projectItemsHeader: ProjectItemsHeaderDTO | null;
  loading: boolean;
  projectItemsLoading: boolean;
  headerLoading: boolean;
  error: string | null;
  projectItemsError: string | null;
  headerError: string | null;
  lastCreatedHeaderId: number | null;
  initialized: boolean; // 🆕 הוסף initialized flag
}

const initialState: BaseItemState = {
  items: [],
  projectItems: [],
  loading: false,
  projectItemsLoading: false,
  headerLoading: false,
  error: null,
  projectItemsError: null,
  headerError: null,
  lastCreatedHeaderId: null,
  projectItemsHeader: null,
  initialized: false, // 🆕 הוסף initialized flag
};

const baseItemSlice = createSlice({
  name: "baseItem",
  initialState,
  reducers: {
    clearError(state) {
      state.error = null;
      state.projectItemsError = null;
      state.headerError = null;
    },
    clearItems(state) {
      state.items = [];
    },
    clearProjectItems(state) {
      state.projectItems = [];
      state.projectItemsHeader = null;
    },
    clearHeaderData(state) {
      state.lastCreatedHeaderId = null;
      state.headerError = null;
    },
    // 🆕 הוסף reducer לניקוי כשמחליפים חברה
    clearItemsOnCompanyChange(state) {
      state.items = [];
      state.projectItems = [];
      state.projectItemsHeader = null;
      state.loading = false;
      state.projectItemsLoading = false;
      state.headerLoading = false;
      state.error = null;
      state.projectItemsError = null;
      state.headerError = null;
      state.lastCreatedHeaderId = null;
      state.initialized = false; // 🆕 איפס את initialized
    },
    // עדכון מקומי של פריט פרויקט
    updateProjectItemLocally(
      state,
      action: PayloadAction<{ itemId: number; field: string; value: any }>,
    ) {
      const { itemId, field, value } = action.payload;
      const itemIndex = state.projectItems.findIndex(
        (item) => item.projectItemID === itemId,
      );
      if (itemIndex !== -1) {
        (state.projectItems[itemIndex] as any)[field] = value;
      }
    },
    updateProjectItemsHeader: (
      state,
      action: PayloadAction<Partial<ProjectItemsHeaderDTO>>,
    ) => {
      if (state.projectItemsHeader) {
        state.projectItemsHeader = {
          ...state.projectItemsHeader,
          ...action.payload,
        };
      } else {
        // 🔥 אם אין header, צור אחד חדש עם כל השדות הנדרשים
        state.projectItemsHeader = {
          projectItemsHdr_ProjectID:
            action.payload.projectItemsHdr_ProjectID ?? 0,
          projectItemsHdr_ItemsCnt:
            action.payload.projectItemsHdr_ItemsCnt ?? 0,
          projectItemsHdr_ItemsSumValue:
            action.payload.projectItemsHdr_ItemsSumValue ?? 0,
          projectItemsHdr_DiscountType:
            action.payload.projectItemsHdr_DiscountType ?? 0,
          projectItemsHdr_DiscountValue:
            action.payload.projectItemsHdr_DiscountValue ?? 0,
          projectItemsHdr_SubTotal:
            action.payload.projectItemsHdr_SubTotal ?? 0,
          projectItemsHdr_VATPercent:
            action.payload.projectItemsHdr_VATPercent ?? 18, // ברירת מחדל - יש להשתמש ב-selectedCompanyVat
          projectItemsHdr_VATValue:
            action.payload.projectItemsHdr_VATValue ?? 0,
          projectItemsHdr_IsRoundedValue:
            action.payload.projectItemsHdr_IsRoundedValue ?? false,
          projectItemsHdr_IsVATZero:
            action.payload.projectItemsHdr_IsVATZero ?? false,
          projectItemsHdr_TotalValue:
            action.payload.projectItemsHdr_TotalValue ?? 0,
          // 🔥 הוסף את השדות החסרים
          projectItemsHdr_Remarks: action.payload.projectItemsHdr_Remarks ?? "",
          projectItemsHdr_UpdatedAt:
            action.payload.projectItemsHdr_UpdatedAt ?? new Date(),
          projectItemsHdr_UpdatedBy:
            action.payload.projectItemsHdr_UpdatedBy ?? 0,
          updatedByName: action.payload.updatedByName ?? "",
        };
      }
    },

    // 🔥 הוסף action לניקוי נתונים לפי projectID
    clearProjectDataForProject(state, action: PayloadAction<number>) {
      const projectID = action.payload;
      // נקה רק אם זה הפרויקט הנוכחי
      if (state.projectItemsHeader?.projectItemsHdr_ProjectID === projectID) {
        state.projectItems = [];
        state.projectItemsHeader = null;
      }
    },
  },
  extraReducers: (builder) => {
    // ==================== BASE ITEMS ====================

    builder.addCase(projectSwitched, (state) => {
      state.projectItems = [];
      state.projectItemsHeader = null;
      state.projectItemsError = null;
      state.headerError = null;
      state.projectItemsLoading = false;
      state.headerLoading = false;
      state.lastCreatedHeaderId = null;
    });

    // Get Base Items
    builder.addCase(getBaseItems.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getBaseItems.fulfilled, (state, action) => {
      state.loading = false;
      state.items = action.payload.baseItems;
      state.error = null;
      state.initialized = true; // 🆕 סמן כמאותחל
    });
    builder.addCase(getBaseItems.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || "שגיאה בקבלת פריטים";
      state.initialized = false; // 🆕 איפס את initialized במקרה של שגיאה
    });

    // Create or Update Base Item
    builder.addCase(createOrUpdateItem.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(createOrUpdateItem.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;

      const requestData = action.meta.arg;
      const responseData = action.payload;

      // בדיקה אם זה עדכון (baseItemID > 0) או יצירה חדשה
      if (requestData.baseItemID && requestData.baseItemID > 0) {
        // עדכון פריט קיים
        const idx = state.items.findIndex(
          (item) => item.baseItemID === requestData.baseItemID,
        );
        if (idx !== -1) {
          state.items[idx] = {
            ...state.items[idx],
            baseItemName: requestData.baseItemName,
            baseItemDescription: requestData.baseItemDescription,
            baseItemImageURL: requestData.baseItemImageURL,
            baseItemSellPrice: requestData.baseItemSellPrice,
            baseItemIsDefault: requestData.baseItemIsDefault,
            // שמירה על הערכים הקיימים של type ו-category names
          };
        }
      } else {
        // יצירת פריט חדש
        const newItem: BaseItemDTO = {
          baseItemID: responseData.baseItemID,
          baseItemName: requestData.baseItemName,
          baseItemDescription: requestData.baseItemDescription,
          baseItemImageURL: requestData.baseItemImageURL,
          baseItemSellPrice: requestData.baseItemSellPrice,
          baseItemIsDefault: requestData.baseItemIsDefault,
          baseItemTypeName: "", // יתעדכן בטעינה הבאה
          baseItemCategoryName: "", // יתעדכן בטעינה הבאה
        };
        state.items.push(newItem);
      }
    });
    builder.addCase(createOrUpdateItem.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || "שגיאה ביצירת/עדכון פריט";
    });

    // Delete Base Item
    builder.addCase(deleteBaseItem.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(deleteBaseItem.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
      // מחיקה מהרשימה
      const id = action.meta.arg.baseItemID;
      state.items = state.items.filter((item) => item.baseItemID !== id);
    });
    builder.addCase(deleteBaseItem.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || "שגיאה במחיקת פריט";
    });

    // ==================== PROJECT ITEMS ====================

    // Get Project Items
    builder.addCase(getProjectItems.pending, (state) => {
      state.projectItemsLoading = true;
      state.projectItemsError = null;
    });
    builder.addCase(getProjectItems.fulfilled, (state, action) => {
      state.projectItemsLoading = false;
      const payload: any = action.payload;
      const nextItems = Array.isArray(payload?.projectItems)
        ? payload.projectItems
        : Array.isArray(payload?.data?.projectItems)
          ? payload.data.projectItems
          : Array.isArray(payload?.data)
            ? payload.data
            : [];
      state.projectItems = nextItems;
      state.projectItemsError = null;
    });
    builder.addCase(getProjectItems.rejected, (state, action) => {
      state.projectItemsLoading = false;
      state.projectItemsError = action.payload || "שגיאה בקבלת פריטי פרויקט";
    });

    // Create or Update Project Item
    builder.addCase(createOrUpdateProjectItem.pending, (state) => {
      state.projectItemsLoading = true;
      state.projectItemsError = null;
    });
    builder.addCase(createOrUpdateProjectItem.fulfilled, (state, action) => {
      state.projectItemsLoading = false;
      state.projectItemsError = null;

      const requestData = action.meta.arg;
      const responseData = action.payload;

      // בדיקה אם זה עדכון (projectItemID > 0) או יצירה חדשה
      if (requestData.projectItemID > 0) {
        // עדכון פריט פרויקט קיים
        const idx = state.projectItems.findIndex(
          (item) => item.projectItemID === requestData.projectItemID,
        );
        if (idx !== -1) {
          state.projectItems[idx] = {
            projectItemID: requestData.projectItemID,
            baseItemID: requestData.baseItemID,
            projectItemDesc: requestData.projectItemDesc,
            projectItemQty: requestData.projectItemQty,
            projectItemUnit: requestData.projectItemUnit,
            projectItemUnitPrice: requestData.projectItemUnitPrice,
            projectItemCurrency: requestData.projectItemCurrency,
            projectItemCurrencyRate: requestData.projectItemCurrencyRate || 1,
            projectItemVATType: requestData.projectItemVATType,
            projectItemVATPercent: requestData.projectItemVATPercent,
            projectItemTotalLine: requestData.projectItemTotalLine,
          };
        }
      } else {
        // יצירת פריט פרויקט חדש
        const newProjectItem: ProjectItemDTO = {
          projectItemID:
            responseData.projectItemID ||
            responseData.projectItemID ||
            Date.now(),
          baseItemID: requestData.baseItemID,
          projectItemDesc: requestData.projectItemDesc,
          projectItemQty: requestData.projectItemQty,
          projectItemUnit: requestData.projectItemUnit,
          projectItemUnitPrice: requestData.projectItemUnitPrice,
          projectItemCurrency: requestData.projectItemCurrency,
          projectItemCurrencyRate: requestData.projectItemCurrencyRate || 1,
          projectItemVATType: requestData.projectItemVATType,
          projectItemVATPercent: requestData.projectItemVATPercent,
          projectItemTotalLine: requestData.projectItemTotalLine,
        };
        state.projectItems.push(newProjectItem);
      }
    });
    builder.addCase(createOrUpdateProjectItem.rejected, (state, action) => {
      state.projectItemsLoading = false;
      state.projectItemsError =
        action.payload || "שגיאה ביצירת/עדכון פריט פרויקט";
      // Debug logging for development only
      if (process.env.NODE_ENV === "development") {
        console.error(
          "🔴 Error saving project item:",
          action.payload,
          "Payload:",
          action.meta.arg,
        );
      }
    });

    // Delete Project Item
    builder.addCase(deleteProjectItem.pending, (state) => {
      state.projectItemsLoading = true;
      state.projectItemsError = null;
    });
    builder.addCase(deleteProjectItem.fulfilled, (state, action) => {
      state.projectItemsLoading = false;
      state.projectItemsError = null;
      // מחיקה מהרשימה
      const id = action.meta.arg.projectItemID;
      state.projectItems = state.projectItems.filter(
        (item) => item.projectItemID !== id,
      );
    });
    builder.addCase(deleteProjectItem.rejected, (state, action) => {
      state.projectItemsLoading = false;
      state.projectItemsError = action.payload || "שגיאה במחיקת פריט פרויקט";
    });

    // ==================== PROJECT ITEMS HEADER ====================

    // Create Project Items Header
    builder.addCase(createProjectItemsHeader.pending, (state) => {
      state.headerLoading = true;
      state.headerError = null;
    });
    builder.addCase(createProjectItemsHeader.fulfilled, (state, action) => {
      state.headerLoading = false;
      state.headerError = null;
      state.lastCreatedHeaderId = action.payload.projectItemsHdrID;
    });
    builder.addCase(createProjectItemsHeader.rejected, (state, action) => {
      state.headerLoading = false;
      state.headerError = action.payload || "שגיאה ביצירת כותרת פריטי פרויקט";
    });
    // Get Project Items Header
    builder.addCase(getProjectItemsHeader.pending, (state) => {
      state.headerLoading = true;
      state.headerError = null;
    });
    builder.addCase(getProjectItemsHeader.fulfilled, (state, action) => {
      state.headerLoading = false;
      state.headerError = null;
      state.projectItemsHeader = action.payload.projectItemsHeader;
    });
    builder.addCase(getProjectItemsHeader.rejected, (state, action) => {
      state.headerLoading = false;
      state.headerError = action.payload || "שגיאה בקבלת פרטי פרויקט";
    });
  },
});

// 🆕 הוסף selectors
export const selectBaseItems = (state: { baseItem: BaseItemState }) =>
  state.baseItem.items;
export const selectBaseItemsLoading = (state: { baseItem: BaseItemState }) =>
  state.baseItem.loading;
export const selectBaseItemsError = (state: { baseItem: BaseItemState }) =>
  state.baseItem.error;
export const selectBaseItemsInitialized = (state: {
  baseItem: BaseItemState;
}) => state.baseItem.initialized;

export const selectProjectItemsHeaderForProject =
  (projectID: number) => (state: { baseItem: BaseItemState }) => {
    const header = state.baseItem.projectItemsHeader;
    if (!header || header.projectItemsHdr_ProjectID !== projectID) {
      return null;
    }
    return header;
  };

// 🔥 selector לבדיקת תקינות הנתונים
export const selectIsProjectDataValid =
  (projectID: number) => (state: { baseItem: BaseItemState }) => {
    const header = state.baseItem.projectItemsHeader;
    const items = state.baseItem.projectItems;

    return {
      hasItems: items && items.length > 0,
      hasHeader: !!header,
      isCorrectProject: header?.projectItemsHdr_ProjectID === projectID,
      isValid: !!(
        header &&
        header.projectItemsHdr_ProjectID === projectID &&
        items?.length > 0
      ),
    };
  };

export const {
  clearError,
  clearItems,
  clearProjectItems,
  clearHeaderData,
  clearItemsOnCompanyChange,
  updateProjectItemLocally,
  updateProjectItemsHeader,
  clearProjectDataForProject,
} = baseItemSlice.actions;
export default baseItemSlice.reducer;
